import { Link } from "@tanstack/react-router";
import { Button, type ButtonProps } from "@/components/ui/button";
import { stageIntentPreloadProps } from "@/features/viewer/preloadStageHost";
import { cn } from "@/lib/utils";

// The landing's one job, as a button: send the visitor into the funnel. `asChild`
// renders the Button's styling onto a real router <Link> (a crawlable anchor), not an
// onClick — so it works without JS and is keyboard/AT-correct for free. Reused by the
// header, hero, closing CTA, and the comparison page so the primary action is
// identical everywhere.
//
// It is a "start / create" CTA, so it carries the browser-local Resume interception
// (ADR-0051): a first-time visitor lands on /upload (the crawlable default, since no
// pointer exists at first paint / for crawlers), while a returning browser with an
// in-flight project is dropped back onto its furthest step — with the "welcome back"
// banner. Interception is CTA-only, never a route guard.
export function DropPhotoCta({
  label,
  size = "lg",
  variant = "default",
  className,
  testId,
}: {
  label: string;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  className?: string;
  testId?: string;
}) {
  // A plain link to the fresh /upload front door, warming the code-split Stage chunk on
  // pointer/focus intent so the `/` → /upload dissolve lands on the real studio, not the
  // loading fallback (#346, ADR-0052) — idempotent (the preloader collapses hover+focus+
  // touchstart to one fetch). The resume-aware furthest-step routing is retired with the
  // browser-local Resume pointer (ADR-0056).
  return (
    <Button
      asChild
      size={size}
      variant={variant}
      className={cn("rounded-full", className)}
      data-testid={testId}
    >
      <Link to="/upload" {...stageIntentPreloadProps}>
        {label}
      </Link>
    </Button>
  );
}
